import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// RENAMING THE CLUB, FROM KLUBBINNSTILLINGER (#766).
//
// `tenants.name` is the canonical club name: the public directory searches it,
// `/me/clubs` lists it, a member's session reports it. Until this, the settings
// screen wrote `club_info_v1.name` — a `/kv` blob nothing else reads for
// identity — so a club that renamed itself could not be found under the name it
// calls itself. That is the whole of #766.
//
// ── THE NAME IT REPLACES TRAVELS WITH THE REQUEST ──────────────────
//
// `expectedName` is what the FORM DISPLAYED, not what the server happens to
// hold now. Without it, two administrators who both opened the screen on
// «Padel24» would both succeed: the second's request reads whatever the first
// committed and swaps against that, replacing a name its author never saw.
// The API requires it for exactly that reason (#774 review).
// ─────────────────────────────────────────────────────────────────────

/**
 * The outcome, as ONE FLAT SHAPE rather than a tagged union.
 *
 * This repository compiles with `strict: false`, under which a boolean
 * discriminant cannot be narrowed by truthiness — `if (!result.ok)` leaves the
 * union unnarrowed and every field access is an error. `code: string | null`
 * needs no narrowing: null means it worked.
 */
export interface RenameClubOutcome {
  /** A machine code from the API, or null on success. Never a sentence. */
  code: string | null;
  /** The name the club now has, on success. */
  name: string;
  /**
   * On `name_changed_meanwhile`: what the club is called NOW, when the server
   * could tell us — so the screen can say what happened rather than «try
   * again». Null when the API could not determine one.
   */
  current: string | null;
}

/** Rename the organization the portal is currently acting in. */
export async function renameClub(name: string, expectedName: string): Promise<RenameClubOutcome> {
  const res = await apiFetch('/organization/name', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, expectedName }),
  });
  const body = (await res.json().catch(() => null)) as {
    name?: unknown;
    error?: unknown;
  } | null;

  if (res.ok) return { code: null, name: String(body?.name ?? name), current: null };

  const code = typeof body?.error === 'string' ? body.error : 'unknown_error';
  // On `name_changed_meanwhile` the API sends the CURRENT name, and omits it
  // when it could not determine one — the row went away between the failed
  // write and its re-read. Null rather than a guess.
  return {
    code,
    name,
    current: typeof body?.name === 'string' ? body.name : null,
  };
}
